import React, { cloneElement, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { CheckCircle2, Loader2, HeartHandshake } from 'lucide-react';

import PublicLayout from '../layouts/PublicLayout';
import Turnstile from '../common/Turnstile';
import { useCreateReferral } from '../../services/leads';
// This public form uses the fixed dark-grey brand theme (hardcoded per branding,
// not the app's light/dark tokens) — shared with /partner-signup + onboarding.
import { INPUT, LABEL, PAGE_BG, useBrandBodyBackground } from './brandTheme';
import VaGovAccountQuestion from './VaGovAccountQuestion';
import { formatUsPhone, isUsPhone, usPhoneDigits } from '../../lib/usPhone';

const HEADING = 'text-base font-semibold text-white';

// Real <label htmlFor> wired to the input: the id is stamped onto the child so
// the label/input association (a11y + tap-to-focus) comes for free.
const Field = ({ id, label, required, children, className }) => (
  <div className={className}>
    <label htmlFor={id} className={LABEL}>
      {label}
      {required ? <span className="text-rose-400"> *</span> : null}
    </label>
    {cloneElement(children, { id })}
  </div>
);

// Public "Refer a Veteran" form (logged out). Posts to the rate-limited public
// referral endpoint, which lands the submission on the Operations Leads board
// under the "Referral Program" channel. The referrer's NAME is always required
// (so the veteran knows who sent them); their contact details are only
// disclosed (and reward eligibility earned) if they opt in to register as a
// partner. The marketing site's "Refer a Veteran" button points here
// (app.veritasratings.com/referral).
const ReferralForm = () => {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    referrerFirstName: '',
    referrerLastName: '',
    referrerEmail: '',
    referrerPhone: '',
    notes: '',
    vaGovAccount: '', // 'yes' | 'no' | 'unsure'
  });
  const [becomePartner, setBecomePartner] = useState(false);
  const [done, setDone] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef(null);

  useBrandBodyBackground();

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setPhone = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: formatUsPhone(e.target.value) }));

  const submitReferral = useCreateReferral(
    () => {
      // Email-verify-first: for partner opt-ins the onboarding link is EMAILED
      // (not returned inline), so we just confirm. The done screen branches on
      // becomePartner to tell them to check their email.
      setDone(true);
    },
    {
      onError: (err) => {
        // Turnstile tokens are single-use — the server consumed this one even on
        // a 4xx, so reset the widget or every retry re-sends the spent token.
        turnstileRef.current?.reset();
        toast.error(
          err?.response?.data?.msg || 'Something went wrong. Please try again.',
        );
      },
    },
  );

  // Phone is required for the veteran (our team calls); their email is optional.
  // The referrer's NAME is always required (anonymous referrals proved
  // unworkable); their email + phone only when they opt in to get paid.
  const veteranPhoneOk = isUsPhone(form.phone);
  const referrerPhoneOk = isUsPhone(form.referrerPhone);
  const referrerNameOk =
    form.referrerFirstName.trim() && form.referrerLastName.trim();
  const referrerComplete =
    referrerNameOk && form.referrerEmail.trim() && referrerPhoneOk;
  const fieldsComplete =
    form.firstName.trim() &&
    form.lastName.trim() &&
    veteranPhoneOk &&
    form.vaGovAccount &&
    (becomePartner ? referrerComplete : referrerNameOk);
  const canSubmit =
    fieldsComplete &&
    (!becomePartner || !!turnstileToken) &&
    !submitReferral.isLoading;

  const onSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit) {
      // When the fields are all fine and only the Turnstile token is missing
      // (unsolved/expired), say so — not "complete your name, email, and phone".
      toast.error(
        fieldsComplete && becomePartner && !turnstileToken
          ? 'Please complete the security check below.'
          : becomePartner
            ? 'To register for the reward, please complete your info — name, email, and phone.'
            : 'Please add the veteran’s name, phone number, and VA.gov answer, plus your name.',
      );
      return;
    }
    const payload = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim() || undefined,
      phone: usPhoneDigits(form.phone) || undefined,
      notes: form.notes.trim() || undefined,
      vaGovAccount: form.vaGovAccount,
      // The referrer's name always rides along — the veteran deserves to know
      // who sent them, and the team needs someone to reference on the call.
      referrerFirstName: form.referrerFirstName.trim(),
      referrerLastName: form.referrerLastName.trim(),
      turnstileToken,
    };
    if (becomePartner) {
      payload.becomePartner = true;
      payload.referrerEmail = form.referrerEmail.trim();
      payload.referrerPhone = usPhoneDigits(form.referrerPhone);
    }
    submitReferral.mutate(payload);
  };

  return (
    <PublicLayout
      background={PAGE_BG}
      footerClassName="text-neutral-300"
      footerVariant="dark"
    >
      <div className="mx-auto max-w-xl py-6">
        <div className="rounded-2xl bg-[#4A4A4E] p-6 shadow-lg sm:p-8">
          <div className="mb-5 text-center">
            <div className="mx-auto mb-2 flex h-11 w-11 items-center justify-center rounded-full bg-white/10">
              <HeartHandshake className="h-6 w-6 text-white" />
            </div>
            <h2 className="text-xl font-bold text-white">Refer a Veteran</h2>
            <p className="text-center text-sm text-neutral-300">
              {done ? (
                'We’ll reach out to your referral within 24–48 hours.'
              ) : (
                <>
                  Know a veteran we can help?
                  <br />
                  Send them our way — our team will reach out within 24–48
                  hours.
                </>
              )}
            </p>
          </div>

          {done ? (
            <div className="rounded-xl bg-emerald-400/10 p-6 text-center">
              <CheckCircle2 className="mx-auto mb-2 h-8 w-8 text-emerald-400" />
              {becomePartner ? (
                <>
                  <h3 className="text-base font-semibold text-white">
                    Thanks! Check your email.
                  </h3>
                  <p className="mt-1 text-sm text-neutral-300">
                    We emailed a link to{' '}
                    <strong className="text-white">{form.referrerEmail}</strong>{' '}
                    to finish becoming a partner — review and e-sign your W-9 +
                    agreement.
                  </p>
                </>
              ) : (
                <>
                  <h3 className="text-base font-semibold text-white">
                    Thank you for the referral!
                  </h3>
                  <p className="mt-1 text-sm text-neutral-300">
                    Someone from our team will reach out to them within 24–48
                    hours.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setForm((f) => ({
                        firstName: '',
                        lastName: '',
                        email: '',
                        phone: '',
                        referrerFirstName: f.referrerFirstName,
                        referrerLastName: f.referrerLastName,
                        referrerEmail: f.referrerEmail,
                        referrerPhone: f.referrerPhone,
                        notes: '',
                        vaGovAccount: '',
                      }));
                      setDone(false);
                    }}
                    className="mt-4 rounded-md bg-black px-4 py-2 text-sm font-medium text-white hover:bg-white/10"
                  >
                    Refer someone else
                  </button>
                </>
              )}
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <h3 className={`mb-4 ${HEADING}`}>Who are you referring?</h3>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {/* Veteran fields intentionally have no autoComplete tokens —
                      the browser would fill in the SUBMITTER's saved info. */}
                  <Field
                    id="referral-firstName"
                    label="Their first name"
                    required
                  >
                    <input
                      className={INPUT}
                      value={form.firstName}
                      onChange={set('firstName')}
                    />
                  </Field>
                  <Field
                    id="referral-lastName"
                    label="Their last name"
                    required
                  >
                    <input
                      className={INPUT}
                      value={form.lastName}
                      onChange={set('lastName')}
                    />
                  </Field>
                  <Field id="referral-phone" label="Their phone" required>
                    <input
                      className={INPUT}
                      type="tel"
                      inputMode="tel"
                      value={form.phone}
                      onChange={setPhone('phone')}
                      placeholder="(904) 555-1212"
                    />
                  </Field>
                  <Field id="referral-email" label="Their email (optional)">
                    <input
                      className={INPUT}
                      type="email"
                      inputMode="email"
                      value={form.email}
                      onChange={set('email')}
                    />
                  </Field>
                </div>
                <p className="mt-2 text-xs text-neutral-300">
                  A phone number is required so our team can reach them. Email
                  is optional.
                </p>
                <div className="mt-4">
                  <VaGovAccountQuestion
                    label="Do they have a VA.gov account?"
                    value={form.vaGovAccount}
                    onChange={(v) => setForm((f) => ({ ...f, vaGovAccount: v }))}
                  />
                </div>
              </div>

              <Field
                id="referral-notes"
                label="Anything we should know? (optional)"
              >
                <textarea
                  value={form.notes}
                  onChange={set('notes')}
                  rows={3}
                  maxLength={2000}
                  className="w-full rounded-md bg-black px-3 py-2 text-sm text-white placeholder:text-neutral-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                />
              </Field>

              {/* Referrer name — always required. Shown here only while the
                  reward box is unchecked; checking it swaps this for the full
                  "Your info" section below (same state, so the name carries). */}
              {!becomePartner ? (
                <div>
                  <h3 className={`mb-1 ${HEADING}`}>Your name</h3>
                  <p className="mb-3 text-xs text-neutral-300">
                    A call from us out of the blue can feel like spam, but
                    hearing your name tells them it&apos;s real.
                  </p>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Field
                      id="referral-referrerFirstName-plain"
                      label="Your first name"
                      required
                    >
                      <input
                        className={INPUT}
                        autoComplete="given-name"
                        value={form.referrerFirstName}
                        onChange={set('referrerFirstName')}
                      />
                    </Field>
                    <Field
                      id="referral-referrerLastName-plain"
                      label="Your last name"
                      required
                    >
                      <input
                        className={INPUT}
                        autoComplete="family-name"
                        value={form.referrerLastName}
                        onChange={set('referrerLastName')}
                      />
                    </Field>
                  </div>
                </div>
              ) : null}

              <div className="mx-auto h-px w-2/3 bg-white/10" />

              {/* Partner opt-in: the referrer's contact details stay private
                  unless they register for the reward (then required). */}
              <div className="rounded-lg bg-black/20 p-4">
                <label className="flex cursor-pointer items-start gap-3">
                  <input
                    type="checkbox"
                    checked={becomePartner}
                    onChange={(e) => {
                      setBecomePartner(e.target.checked);
                      // The Turnstile widget only mounts while this box is
                      // checked — drop any held token when it unmounts so a
                      // stale one can't ride along on a later submit.
                      if (!e.target.checked) setTurnstileToken('');
                    }}
                    className="mt-0.5 h-4 w-4 shrink-0 accent-emerald-400"
                  />
                  <span>
                    <span className="block text-sm font-medium text-white">
                      Want to get paid for this referral?
                    </span>
                    <span className="mt-0.5 block text-xs text-neutral-300">
                      Refer a veteran who’s new to us and earn $250 when they
                      become a client and complete their claim.
                    </span>
                  </span>
                </label>

                {becomePartner ? (
                  <div className="mt-4">
                    <h3 className={`mb-3 ${HEADING}`}>Your info</h3>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <Field
                        id="referral-referrerFirstName"
                        label="Your first name"
                        required
                      >
                        <input
                          className={INPUT}
                          autoComplete="given-name"
                          value={form.referrerFirstName}
                          onChange={set('referrerFirstName')}
                        />
                      </Field>
                      <Field
                        id="referral-referrerLastName"
                        label="Your last name"
                        required
                      >
                        <input
                          className={INPUT}
                          autoComplete="family-name"
                          value={form.referrerLastName}
                          onChange={set('referrerLastName')}
                        />
                      </Field>
                      <Field
                        id="referral-referrerEmail"
                        label="Your email"
                        required
                      >
                        <input
                          className={INPUT}
                          type="email"
                          inputMode="email"
                          autoComplete="email"
                          value={form.referrerEmail}
                          onChange={set('referrerEmail')}
                        />
                      </Field>
                      <Field
                        id="referral-referrerPhone"
                        label="Your phone"
                        required
                      >
                        <input
                          className={INPUT}
                          type="tel"
                          inputMode="tel"
                          autoComplete="tel"
                          value={form.referrerPhone}
                          onChange={setPhone('referrerPhone')}
                          placeholder="(904) 555-1212"
                        />
                      </Field>
                    </div>
                    <p className="mt-2 text-xs text-neutral-300">
                      We’ll use this to send your $250 reward.
                    </p>
                    <div className="mt-4">
                      <Turnstile
                        ref={turnstileRef}
                        onToken={setTurnstileToken}
                      />
                    </div>
                  </div>
                ) : (
                  <p className="mb-0 mt-3 text-center text-xs text-neutral-400">
                    — Please check the box before submitting to stay eligible to
                    get paid! —
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={submitReferral.isLoading}
                className="inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-white/10 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-neutral-200 hover:!text-black disabled:opacity-50"
              >
                {submitReferral.isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : null}
                Submit referral
              </button>
              <p className="mb-0 mt-3 text-center text-[11px] leading-snug text-neutral-500">
                By submitting, you confirm you have your referral&apos;s
                permission — and we may share their high-level progress (like
                whether your reward was earned) with you as the referrer.
              </p>
            </form>
          )}
        </div>
      </div>
    </PublicLayout>
  );
};

export default ReferralForm;
